import { motion } from "framer-motion";
import { Radar, ShieldCheck, Clock, ArrowRight, Calculator } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1563841930606-67e2bce48b78?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHw0fHxjb25jZXJ0JTIwY3Jvd2QlMjBmZXN0aXZhbHxlbnwwfHx8fDE3ODg5MjU2MTV8MA&ixlib=rb-4.1.0&q=85";

const badges = [
  { icon: ShieldCheck, label: "100% Background-Checked" },
  { icon: ShieldCheck, label: "Fully Insured & Compliant" },
  { icon: Clock, label: "3-Hour Rapid Deployment" },
];

export default function HeroSection({ onQuote, onEstimator }) {
  return (
    <section className="relative min-h-[94vh] flex items-center grain-overlay">
      <img
        src={HERO_IMG}
        alt="Concert crowd under stage lights"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/25" />
      <div className="absolute inset-0 hero-glow" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 w-full">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <span
            data-testid="hero-eyebrow"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-mono-tactical font-semibold text-sky-400 border border-sky-500/30 bg-sky-500/10 rounded-full px-4 py-1.5"
          >
            <Radar className="w-3.5 h-3.5 radar-dot rounded-full" />
            Tier-1 Event & Arena Staffing
          </span>

          <h1
            data-testid="hero-headline"
            className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[1.05] text-white"
          >
            Elite Frontline Teams for{" "}
            <span className="text-sky-500">Arena Shows, Stadium Games</span>{" "}
            & Major Festivals
          </h1>

          <p
            data-testid="hero-subhead"
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl"
          >
            Sora deploys vetted, uniformed, event-ready staff — ushers,
            ticket scanners, VIP hospitality, concessions, and crowd-flow teams —
            with a zero no-show guarantee. You run the show. We run the floor.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-quote-button"
              onClick={onQuote}
              className="group inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 text-[#090A0F] font-bold rounded-sm px-7 py-3.5 transition-all duration-200 hover:gap-3"
            >
              Request Staff Quote
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              data-testid="hero-cta-calculator-button"
              onClick={onEstimator}
              className="inline-flex items-center gap-2 border border-white/25 hover:border-sky-500/60 hover:text-sky-400 text-white font-semibold rounded-sm px-7 py-3.5 transition-colors duration-200"
            >
              <Calculator className="w-4 h-4" />
              Calculate Crew Size
            </button>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {badges.map((b) => (
              <span
                key={b.label}
                className="inline-flex items-center gap-2 text-sm text-slate-300"
              >
                <b.icon className="w-4 h-4 text-sky-500" />
                {b.label}
              </span>
            ))}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        className="hidden xl:block absolute bottom-24 right-12 bg-[#11131B]/85 backdrop-blur-xl border border-white/10 rounded-md p-5 w-72"
        data-testid="hero-telemetry-card"
      >
        <p className="font-mono-tactical text-[11px] uppercase tracking-widest text-sky-400">
          Live Shift Telemetry
        </p>
        <p className="mt-2 font-display text-lg font-bold text-white">
          Arena B — Gate 4
        </p>
        <div className="mt-3 space-y-2 text-sm text-slate-300">
          <div className="flex justify-between">
            <span>Ushers deployed</span>
            <span className="font-mono-tactical text-white">42</span>
          </div>
          <div className="flex justify-between">
            <span>Check-in rate</span>
            <span className="font-mono-tactical text-emerald-400">99.8%</span>
          </div>
          <div className="flex justify-between">
            <span>Standby reserve</span>
            <span className="font-mono-tactical text-white">6 ready</span>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
